import {
  FeatureFlagName,
  FeatureFlagAccessLevel,
} from "../services/api/__generated__/backend_gateway-types";

// Re-export the types for convenience
export type { FeatureFlagName, FeatureFlagAccessLevel };

/**
 * Type for the feature flags as they appear in the frontend
 * This represents the boolean converted features that the frontend uses
 */
export type ClientFeatureFlags = Partial<Record<FeatureFlagName, boolean>>;

/**
 * Type for the raw feature flags config (as stored in backend)
 */
export type FeatureFlagsConfig = Partial<
  Record<FeatureFlagName, FeatureFlagAccessLevel>
>;

/**
 * Runtime validation for JSON parsing - checks if string is a valid feature flag name
 */
export function isValidFeatureFlagName(name: string): name is FeatureFlagName {
  return Object.values(FeatureFlagName).includes(name as FeatureFlagName);
}

/**
 * Runtime validation for JSON parsing - checks if string is a valid access level
 */
export function isValidFeatureFlagAccessLevel(
  level: string
): level is FeatureFlagAccessLevel {
  return Object.values(FeatureFlagAccessLevel).includes(
    level as FeatureFlagAccessLevel
  );
}

/**
 * Converts raw feature flags to client boolean features
 * Supports both string access levels ("ADMIN", "ROOT") and boolean values
 */
export function convertToClientFeatures(
  rawFeatures: string | undefined | null
): ClientFeatureFlags {
  if (!rawFeatures) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawFeatures);
    const clientFeatures: ClientFeatureFlags = {};

    for (const [key, value] of Object.entries(parsed)) {
      if (isValidFeatureFlagName(key)) {
        // Handle string access levels (preferred format)
        if (typeof value === "string" && isValidFeatureFlagAccessLevel(value)) {
          clientFeatures[key as FeatureFlagName] = true; // Convert any valid access level to true for client
        }
        // Handle boolean values (legacy format)
        else if (typeof value === "boolean") {
          clientFeatures[key as FeatureFlagName] = value;
        }
        // Log invalid values for debugging
        else {
          console.warn(
            `Invalid feature flag value for ${key}:`,
            value,
            `Expected string access level (ADMIN or ROOT) or boolean, got ${typeof value}`
          );
        }
      } else {
        console.warn(`Invalid feature flag name: ${key}`);
      }
    }

    return clientFeatures;
  } catch (error) {
    console.warn("Failed to parse feature flags:", error);
    return {};
  }
}

/**
 * Type guard to ensure features object has the correct typing
 */
export function validateClientFeatures(
  features: unknown
): features is ClientFeatureFlags {
  if (typeof features !== "object" || features === null) {
    return false;
  }

  const obj = features as Partial<Record<FeatureFlagName, unknown>>;

  for (const [key, value] of Object.entries(obj)) {
    if (!isValidFeatureFlagName(key) || typeof value !== "boolean") {
      return false;
    }
  }

  return true;
}
